import {useCallback, useEffect} from 'react'
import {shallowEqual} from 'react-redux'

import {useQueryParam, useSetQueryParams} from '../../../../hooks/routes'
import {useIsAutodetectTimeZone} from '../../../../hooks/useIsAutodetectTimeZone'
import {getBuild, getExtensionEndpoint} from '../../../../selectors'
import {fetchBuildLogMessages} from '../BuildLog.actions'
import {
  getBuildLogMessagesReady,
  getBuildLogMessagesLoading,
  getFullBuildLog,
  getFullLogState,
  getMessagesLoadStates,
  getSoftWrapLinesInBuildlog,
  getShowLog,
  getRelativeTimeInBuildlog,
} from '../BuildLog.selectors'
import type {
  FetchMessagesParams,
  FullLogScrollBarInfo,
  FullLogTarget,
  LogView,
} from '../BuildLog.types'
import {getKeyForBuildLogData} from '../BuildLog.utils'
import BuildLogInfo from '../BuildLogInfo/BuildLogInfo'

import {
  getBuilLogSearchQuery,
  getBuilLogSearchFoundId,
  getBuilLogSearchMatchPosition,
} from './BuildLogHeader/BuildLogSearch/BuildLogSearch.selectors'
import FullBuildLog from './FullBuildLog'
import {
  updateFullLogState,
  updateFullLogShowed,
  setFullLogScrollBarInfo,
  resetLogView,
} from './FullBuildLog.actions'
import type {OwnProps} from './FullBuildLog.types'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

export default function FullBuildLogContainer({target, className}: OwnProps) {
  const {buildId, expandState, focusLine, expandAll, filter, lastLoadingDirection, logView} =
    useAppSelector(state => getFullLogState(state, target), shallowEqual)
  const build = useAppSelector(state => getBuild(state, buildId))
  const buildReady = build != null
  const running = build?.state === 'running'
  const buildLogKey = getKeyForBuildLogData({
    type: 'full',
    target,
  })
  const {messages, holeIndexes, expandStateSet, expandableChildrenMap, maxMessageLength} =
    useAppSelector(state => getFullBuildLog(state, buildLogKey, target, running))

  const ready = useAppSelector(state => getBuildLogMessagesReady(state, buildLogKey))
  const extensionEnabled = useAppSelector(state =>
    Boolean(getExtensionEndpoint(state, 'fetchExpandedBuildLogMessage')),
  )
  const loading = useAppSelector(state => getBuildLogMessagesLoading(state, buildLogKey))
  const messagesLoadState = useAppSelector(state => getMessagesLoadStates(state, buildLogKey))
  const softWrapLines = useAppSelector(state => getSoftWrapLinesInBuildlog(state))
  const relativeTime = useAppSelector(state => getRelativeTimeInBuildlog(state))

  const searchQuery = useAppSelector(state => getBuilLogSearchQuery(state, target))
  const searchFoundId = useAppSelector(state => getBuilLogSearchFoundId(state, target))
  const searchMatchPosition = useAppSelector(state => getBuilLogSearchMatchPosition(state, target))
  const isAutodetectTimeZone = useIsAutodetectTimeZone()

  const dispatch = useAppDispatch()

  const fetchMessages = useCallback(
    (params: FetchMessagesParams) => dispatch(fetchBuildLogMessages(params)),
    [dispatch],
  )
  const updateLogState = useCallback(
    (options: {
      target: FullLogTarget
      focusLine?: number | null | undefined
      expandState?: ReadonlyArray<number> | null | undefined
      expandAll?: boolean
    }) => dispatch(updateFullLogState(options)),
    [dispatch],
  )
  const onShowMessages = useCallback(
    (options: {
      target: FullLogTarget
      firstId: number | null | undefined
      firstDate: string | null | undefined
      lastDate: string | null | undefined
      lastId: number | null | undefined
    }) => dispatch(updateFullLogShowed(options)),
    [dispatch],
  )
  const setScrollBarInfo = useCallback(
    (targetParam: FullLogTarget, info: FullLogScrollBarInfo) =>
      dispatch(setFullLogScrollBarInfo(targetParam, info)),
    [dispatch],
  )

  const logViewParam = useQueryParam('logView')
  const setQueryParams = useSetQueryParams()
  const showLog = useAppSelector(getShowLog)
  useEffect(() => {
    if (logView != null && logView !== logViewParam) {
      setQueryParams({logView, showLog})
    }
  }, [logView, logViewParam, setQueryParams, showLog])
  useEffect(
    () => () => {
      dispatch(resetLogView())
    },
    [dispatch],
  )

  if (buildId == null) {
    return null
  }

  if (!buildReady) {
    return <BuildLogInfo type="loading" target={target} />
  }

  if (!extensionEnabled) {
    return <BuildLogInfo type="disabled" target={target} />
  }

  return (
    <FullBuildLog
      target={target}
      className={className}
      extensionEnabled={extensionEnabled}
      ready={ready}
      loading={loading}
      messagesLoadState={messagesLoadState}
      queued={build?.state === 'queued'}
      softWrapLines={softWrapLines}
      relativeTime={relativeTime}
      searchQuery={searchQuery}
      searchFoundId={searchFoundId}
      searchMatchPosition={searchMatchPosition}
      isAutodetectTimeZone={isAutodetectTimeZone}
      buildReady={buildReady}
      running={running}
      buildId={buildId}
      focusLine={focusLine}
      expandState={expandState}
      expandStateSet={expandStateSet}
      expandableChildrenMap={expandableChildrenMap}
      expandAll={expandAll}
      filter={filter}
      logView={logView ?? (logViewParam as LogView)}
      messages={messages}
      holeIndexes={holeIndexes}
      maxMessageLength={maxMessageLength}
      lastLoadingDirection={lastLoadingDirection}
      fetchMessages={fetchMessages}
      updateLogState={updateLogState}
      onShowMessages={onShowMessages}
      setScrollBarInfo={setScrollBarInfo}
    />
  )
}
